import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Typography from "@mui/material/Typography"
import { useEffect, useRef, useState } from "react"
import ReactCrop from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"

import DialogHeader from "./DialogHeader"

const CROP_MODAL_MAX_WIDTH_PX = 520

export default function SignatureImageCropDialog({
  open,
  imgData,
  onClose,
  onCropFinished,
}) {
  const [imageCrop, setImageCrop] = useState({
    unit: "%",
    x: 5,
    y: 5,
    width: 90,
    height: 90,
  })
  const imageElemRef = useRef()
  const [displayWidth, setDisplayWidth] = useState(0)
  const canvasRef = useRef()

  function applyCrop(crop) {
    if (!crop?.width || !crop?.height) {
      return
    }

    const canvas2dContext = canvasRef.current?.getContext("2d")
    const image = imageElemRef.current
    if (!canvas2dContext || !image) {
      return
    }

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    const pixelRatio = window.devicePixelRatio || 1

    const cropX = crop.unit === "%" ? (crop.x * image.width) / 100 : crop.x
    const cropY = crop.unit === "%" ? (crop.y * image.height) / 100 : crop.y
    const cropWidth =
      crop.unit === "%" ? (crop.width * image.width) / 100 : crop.width
    const cropHeight =
      crop.unit === "%" ? (crop.height * image.height) / 100 : crop.height

    canvasRef.current.width = Math.floor(cropWidth * scaleX * pixelRatio)
    canvasRef.current.height = Math.floor(cropHeight * scaleY * pixelRatio)
    canvas2dContext.imageSmoothingQuality = "high"
    canvas2dContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    canvas2dContext.drawImage(
      image,
      cropX * scaleX,
      cropY * scaleY,
      cropWidth * scaleX,
      cropHeight * scaleY,
      0,
      0,
      cropWidth * scaleX,
      cropHeight * scaleY
    )

    onCropFinished(canvasRef.current.toDataURL("image/png"))
  }

  function handleApplyClick() {
    applyCrop(imageCrop)
    onClose()
  }

  useEffect(() => {
    if (!imgData) {
      setDisplayWidth(0)
      return
    }

    const image = new Image()
    image.src = imgData
    image.onload = () => {
      const scale = Math.min(1, CROP_MODAL_MAX_WIDTH_PX / image.naturalWidth)
      setDisplayWidth(Math.round(image.naturalWidth * scale))
      setImageCrop({
        unit: "%",
        x: 5,
        y: 5,
        width: 90,
        height: 90,
      })
    }
  }, [imgData])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogHeader title="Adjust signature image" onClose={onClose} />
      <DialogContent
        sx={{
          px: 2,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "background.default"
              : theme.palette.grey[50],
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Drag the handles to crop and frame your logo or photo before it is
          added to your signature.
        </Typography>
        <Box
          sx={{
            width: "100%",
            maxWidth: displayWidth > 0 ? displayWidth : CROP_MODAL_MAX_WIDTH_PX,
            alignSelf: "center",
          }}
        >
          <ReactCrop crop={imageCrop} onChange={(crop) => setImageCrop(crop)}>
            <img
              alt="Crop signature"
              ref={imageElemRef}
              src={imgData}
              style={{
                display: "block",
                maxWidth: "100%",
                width: displayWidth > 0 ? displayWidth : "100%",
                height: "auto",
              }}
            />
          </ReactCrop>
        </Box>
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApplyClick}
          disabled={!imageCrop?.width || !imageCrop?.height}
        >
          Insert image
        </Button>
      </DialogActions>
    </Dialog>
  )
}
